import React, { useState } from 'react';
import { Modal } from '../Modal/Modal.component';
import { formatPhone, formatDate } from './helpers';
import { Grid, IconButton } from '@material-ui/core';
import { EditUserName } from '../UserProfile/EditUserName.component';
import { EditUserPhone } from '../UserProfile/EditUserPhone.component';
import { EditUserPassword } from '../UserProfile/EditUserPassword';
import { EditUserEmail } from './EditUserEmail.component';
import usePermission from '../../hooks/usePermission';
import { UserPermission } from '../../enum/userPermissions';
import EditIcon from '@material-ui/icons/Edit';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import { IUserProfile } from './IUserProfile.types';
import { useStyles } from './UserProfile.styles';
import TooltipOne from '../../components/TooltipOne/TooltipOne.component';
import useSessionUser from '../../hooks/useSessionUser';

interface UserProfileProps {
  user?: IUserProfile;
  open: boolean;
  handleOpenUserProfile: () => void;
  onClose(): void;
}

export const UserProfile: React.FC<UserProfileProps> = (props) => {
  const classes = useStyles();
  const { user } = useSessionUser();

  const [openEditUserName, setOpenEditUserName] = useState<boolean>(false);
  const [openEditUserPhone, setOpenEditUserPhone] = useState<boolean>(false);
  const [openEditUserPassword, setOpenEditUserPassword] = useState<boolean>(false);
  const [openEditUserEmail, setOpenEditUserEmail] = useState<boolean>(false);

  const handleOpenEditUserName = () => {
    props.onClose();
    setOpenEditUserName(true);
  };

  const handleOpenEditUserPhone = () => {
    props.onClose();
    setOpenEditUserPhone(true);
  };

  const handleOpenEditUserPassword = () => {
    props.onClose();
    setOpenEditUserPassword(true);
  };

  const handleOpenEditUserEmail = () => {
    props.onClose();
    setOpenEditUserEmail(true);
  };

  const handleEditUserNameClose = () => {
    setOpenEditUserName(false);
  };

  const handleEditUserPhoneClose = () => {
    setOpenEditUserPhone(false);
  };

  const handleEditUserEmailClose = () => {
    setOpenEditUserEmail(false);
  };

  const handleEditUserPasswordClose = () => {
    setOpenEditUserPassword(false);
  };

  const handleNavBack = () => {
    props.handleOpenUserProfile();
  };

  const hasSelfUpdatePermision = usePermission(UserPermission.UserSelfUpdate);
  const hasSelfPassChangePermission = usePermission(UserPermission.UserSelfPasswordChange);

  return (
    <>
      {props.user && (
        <Modal open={props.open} size="sm" title="Mój profil" xCloseButton={props.onClose} autoHeight={true}>
          <Grid container className={classes.container}>
            <Grid item xs={5} className={classes.item}>
              <div className={classes.leftText}>Imię i nazwisko</div>
            </Grid>
            <Grid item xs={5} className={classes.item}>
              <div className={classes.rightText}>
                {props.user.name} {props.user.surname}
              </div>
            </Grid>
            <Grid item xs={2} className={classes.editIconContainer}>
              {hasSelfUpdatePermision && (
                <TooltipOne title="Edytuj dane" placement="top">
                  <IconButton style={{padding: '0'}} onClick={handleOpenEditUserName}>
                  <EditIcon className={classes.editIcon} aria-label="edycja danych" />
                  </IconButton>
                </TooltipOne>
              )}
            </Grid>
            <Grid item xs={12}>
              <hr className={classes.line}></hr>
            </Grid>

            <Grid item xs={5} className={classes.item}>
              <div className={classes.leftText}>Nr telefonu</div>
            </Grid>
            <Grid item xs={5} className={classes.item}>
              <div className={classes.rightText}>{formatPhone(props.user.phone)}</div>
            </Grid>
            <Grid item xs={2} className={classes.editIconContainer}>
              {hasSelfUpdatePermision && (
                <TooltipOne title="Edytuj Nr telefonu" placement="top">
                  <IconButton style={{padding: '0'}} onClick={handleOpenEditUserPhone}>
                  <EditIcon
                    className={classes.editIcon}
                    aria-label="edycja nr telefonu"
                  />
                  </IconButton>
                </TooltipOne>
              )}
            </Grid>

            <Grid item xs={12}>
              <hr className={classes.line}></hr>
            </Grid>

            <Grid item xs={5} className={classes.item}>
              <div className={classes.leftText}>E-mail</div>
            </Grid>
            <Grid item xs={5} className={classes.item}>
              <div className={classes.rightText}>{props.user.email}</div>
            </Grid>
            <Grid item xs={1} className={classes.editIconContainer}>
              {props.user.temporaryEmail !== '' && props.user.temporaryEmail !== props.user.email && (
                <TooltipOne
                  title={`Oczekiwanie na autoryzację adresu e-mail: ${props.user.temporaryEmail}`}
                  placement="top"
                >
                  <IconButton style={{padding: '0'}}>
                  <AutorenewIcon className={classes.authIcon} />
                  </IconButton>
                </TooltipOne>
              )}
            </Grid>
            <Grid item xs={1} className={classes.editIconContainer}>
              {hasSelfUpdatePermision && (
                <TooltipOne title="Edytuj adres E-mail" placement="top">
                  <IconButton style={{padding: '0'}} onClick={handleOpenEditUserEmail}>
                  <EditIcon
                    className={classes.editIcon}
                    aria-label="edycja adresu e-mail"
                  />
                  </IconButton>
                </TooltipOne>
              )}
            </Grid>

            <Grid item xs={12}>
              <hr className={classes.line}></hr>
            </Grid>

            <Grid item xs={5} className={classes.item}>
              <div className={classes.leftText}>Hasło</div>
            </Grid>
            <Grid item xs={5} className={classes.item}>
              <TooltipOne title={formatDate(props.user.passLastUpd)} placement="top">
                <div className={classes.rightText}>***********</div>
              </TooltipOne>
            </Grid>
            <Grid item xs={2} className={classes.editIconContainer}>
              {hasSelfPassChangePermission && (
                <TooltipOne title="Edytuj hasło" placement="top">
                  <IconButton style={{padding: '0'}} onClick={handleOpenEditUserPassword}>
                  <EditIcon
                    className={classes.editIcon}
                    aria-label="edycja hasła"
                  />
                  </IconButton>
                </TooltipOne>
              )}
            </Grid>

            <Grid item xs={12}>
              <hr className={classes.line}></hr>
            </Grid>

            <Grid item xs={5} className={classes.item}>
              <div className={classes.leftText}>PESEL</div>
            </Grid>
            <Grid item xs={5} className={classes.item}>
              <div className={classes.rightText}>{props.user.pesel}</div>
            </Grid>

            <Grid item xs={12}>
              <hr className={classes.line}></hr>
            </Grid>

            <Grid item xs={5} className={classes.item}>
              <div className={classes.leftText}>Status konta</div>
            </Grid>
            <Grid item xs={5} className={classes.item}>
              <div className={classes.rightText}>{props.user.status ? 'Aktywny' : 'Nieaktywny'}</div>
            </Grid>
            <Grid item xs={12}>
              <hr className={classes.line}></hr>
            </Grid>
          </Grid>
        </Modal>
      )}
      {openEditUserName && (
        <EditUserName
          open={openEditUserName}
          onClose={handleEditUserNameClose}
          onNavBack={handleNavBack}
          user={props.user}
        />
      )}
      {user?.profile?.sub && (
        <>
          {openEditUserPhone && (
            <EditUserPhone
              open={openEditUserPhone}
              onClose={handleEditUserPhoneClose}
              onNavBack={handleNavBack}
              user={props.user}
              userId={user.profile.sub}
            />
          )}
          {openEditUserEmail && (
            <EditUserEmail
              open={openEditUserEmail}
              onClose={handleEditUserEmailClose}
              onNavBack={handleNavBack}
              user={props.user}
              userId={user.profile.sub}
            />
          )}
        </>
      )}
      {openEditUserPassword && (
        <EditUserPassword
          open={openEditUserPassword}
          onClose={handleEditUserPasswordClose}
          onNavBack={handleNavBack}
          user={props.user}
        />
      )}
    </>
  );
};
